import { useState, useEffect, useCallback } from 'react';
import { ProjectData } from '../types';
import {
  migrateWizardLocalStorage,
  readWizardLocalStorage,
  removeWizardLocalStorage,
  wizardStorageKey,
  type WizardLocalStorageData,
} from '../utils/wizardLocalStorage';

interface UseLocalStorageReturn {
  saveToLocalStorage: (data: Partial<ProjectData['formData']>, currentStep: number, completedSteps: number[]) => void;
  loadFromLocalStorage: () => WizardLocalStorageData | null;
  clearLocalStorage: () => void;
  migrateFromPendingKey: (
    previousPendingKey: string,
    options?: { removeSource?: boolean }
  ) => WizardLocalStorageData | null;
  removePendingKey: (previousPendingKey: string) => void;
  hasLocalData: boolean;
  lastLocalSave: Date | null;
}

export function useLocalStorage(jiraKey: string): UseLocalStorageReturn {
  const [hasLocalData, setHasLocalData] = useState(false);
  const [lastLocalSave, setLastLocalSave] = useState<Date | null>(null);

  // Don't create storage key if jiraKey is empty
  const storageKey = jiraKey ? wizardStorageKey(jiraKey) : null;

  // Check if local data exists on mount
  useEffect(() => {
    if (!storageKey) return; // Skip if no valid storage key
    
    try {
      const localData = localStorage.getItem(storageKey);
      if (localData) {
        const parsed = JSON.parse(localData);
        setHasLocalData(true);
        setLastLocalSave(new Date(parsed.lastSaved));
      }
    } catch (error) {
      // Silently handle localStorage errors (private browsing, etc.)
      console.warn('Local storage not available:', error);
      setHasLocalData(false);
    }
  }, [storageKey]);

  const saveToLocalStorage = useCallback((
    formData: Partial<ProjectData['formData']>,
    currentStep: number,
    completedSteps: number[]
  ) => {
    // Skip if no valid storage key
    if (!storageKey || !jiraKey) {
      return;
    }

    try {
      // Load existing data and merge with new data to avoid overwriting
      let existingFormData: Partial<ProjectData['formData']> = {};
      const existingData = localStorage.getItem(storageKey);
      if (existingData) {
        const parsed = JSON.parse(existingData);
        existingFormData = parsed.formData || {};
      }
      
      // Merge new formData with existing formData
      const mergedFormData = { ...existingFormData, ...formData };
      
      const data: WizardLocalStorageData = {
        formData: mergedFormData,
        currentStep,
        completedSteps,
        lastSaved: new Date().toISOString(),
        jiraKey,
      };

      localStorage.setItem(storageKey, JSON.stringify(data));
      setHasLocalData(true);
      setLastLocalSave(new Date());
      
      // Only log occasionally to reduce spam
      if (Math.random() < 0.05) { // Log 5% of saves
        console.log('💾 Progress saved to local storage');
      }
    } catch (error) {
      // Silently handle localStorage errors (private browsing, quota exceeded, etc.)
      console.warn('Local storage save failed:', error);
    }
  }, [storageKey, jiraKey]);

  const loadFromLocalStorage = useCallback((): WizardLocalStorageData | null => {
    // Skip if no valid storage key
    if (!storageKey || !jiraKey) {
      return null;
    }

    try {
      const parsed = readWizardLocalStorage(jiraKey);
      
      if (parsed) {
        setHasLocalData(true);
        setLastLocalSave(new Date(parsed.lastSaved));
        console.log('📂 Loaded progress from local storage');
        return parsed;
      }
    } catch (error) {
      // Silently handle localStorage errors
      console.warn('Local storage load failed:', error);
    }
    return null;
  }, [storageKey, jiraKey]);

  const migrateFromPendingKey = useCallback(
    (
      previousPendingKey: string,
      options?: { removeSource?: boolean }
    ): WizardLocalStorageData | null => {
      if (!jiraKey || !previousPendingKey) return null;
      const migrated = migrateWizardLocalStorage(previousPendingKey, jiraKey, options);
      if (migrated) {
        setHasLocalData(true);
        setLastLocalSave(new Date(migrated.lastSaved));
        console.log(`🔄 IMA-20: migrated wizard localStorage ${previousPendingKey} → ${jiraKey}`);
      }
      return migrated;
    },
    [jiraKey]
  );

  const removePendingKey = useCallback((previousPendingKey: string) => {
    if (!previousPendingKey) return;
    removeWizardLocalStorage(previousPendingKey);
  }, []);

  const clearLocalStorage = useCallback(() => {
    // Skip if no valid storage key
    if (!storageKey) {
      return;
    }

    try {
      localStorage.removeItem(storageKey);
      setHasLocalData(false);
      setLastLocalSave(null);
      console.log('🗑️ Local storage cleared');
    } catch (error) {
      // Silently handle localStorage errors
      console.warn('Local storage clear failed:', error);
    }
  }, [storageKey]);

  return {
    saveToLocalStorage,
    loadFromLocalStorage,
    clearLocalStorage,
    migrateFromPendingKey,
    removePendingKey,
    hasLocalData,
    lastLocalSave,
  };
}
